import React from 'react';

import { useIntl } from 'react-intl-next';

import { MenuList } from '@atlaskit/navigation-system';
import { getHiddenItems, GlobalMoreMenu } from '@atlassian/global-more-menu';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type { NavItemConfigInternal } from '../../common/types';
import { useNavAnalytics } from '../../common/utils/use-nav-analytics';
import { useOptions } from '../../controllers/options-context';

import { CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID, MORE_MENU_TEST_ID } from './constants';
import { CustomizeSidebarMenuItem } from './customize-sidebar-menu-item';
import { messages } from './messages';
import MoreMenuItem from './more-menu-item';
import { useMoreMenuDragAndDrop } from './use-more-menu-drag-and-drop';

export interface MoreMenuProps {
	items: Record<string, NavItemConfigInternal>;
	shortcutItems?: Record<string, GlobalShortcutsItem>;
	itemIds: string[];
	shortcutItemIds: string[];
	peekedItemId: string | null | undefined;
	onPeek: (id: string) => void;
	appLabel: string;
	onClickMoreMenuTrigger?: () => void;
	onClickCustomizeSidebarMenuItem?: () => void;
}

type MenuItemComponentType = React.ComponentProps<typeof GlobalMoreMenu>['menuItemComponent'];

export default function MoreMenu({
	items,
	shortcutItems = {},
	itemIds,
	shortcutItemIds,
	peekedItemId,
	onPeek,
	appLabel,
	onClickMoreMenuTrigger,
	onClickCustomizeSidebarMenuItem,
}: MoreMenuProps): React.ReactNode {
	const intl = useIntl();
	const { fireClickSideNavItemEvent } = useNavAnalytics();
	const { l1DragAndDropEnabled } = useOptions();

	const menuItemComponent: MenuItemComponentType = React.useCallback(
		({ closeMenu, menuId }: React.ComponentProps<MenuItemComponentType>) => {
			const fireMoreMenuItemClickedEvent = () => {
				const isShortcutItem = shortcutItemIds.includes(menuId);
				const currentGroup = isShortcutItem ? shortcutItemIds : itemIds;
				const index = currentGroup.findIndex((key) => key === menuId);
				closeMenu();
				fireClickSideNavItemEvent({
					navLayoutComponent: 'postMain',
					group: isShortcutItem ? 'appShortcut' : 'appSpecific',
					groupItemsCount: currentGroup.length,
					groupItemsIndex: index,
					groupItem: menuId,
					isInMore: true,
					navItemsCount: itemIds.length + shortcutItemIds.length + 1, // +1 for customise sidebar item
					navItemsIndex: isShortcutItem ? itemIds.length - 1 + index : index, // add appSpecific items count for shortcut item's index
				});
			};

			return (
				<MoreMenuItem
					id={menuId}
					key={menuId}
					items={items}
					shortcutItems={shortcutItems}
					onClick={fireMoreMenuItemClickedEvent}
					onPeek={onPeek}
				/>
			);
		},
		[itemIds, items, fireClickSideNavItemEvent, onPeek, shortcutItemIds, shortcutItems],
	);

	const productItemsData = React.useMemo(() => {
		return itemIds.map((menuId) => ({
			menuId,
			visible: false,
		}));
	}, [itemIds]);

	const appItemsData = React.useMemo(() => {
		return shortcutItemIds.map((menuId) => ({
			menuId,
			visible: false,
		}));
	}, [shortcutItemIds]);

	const { shouldShowMoreMenu } = getHiddenItems(
		productItemsData,
		appItemsData,
		peekedItemId ? [peekedItemId] : [],
	);
	const { dropIndicator, dropTargetRef, moreButtonRef } =
		useMoreMenuDragAndDrop(shouldShowMoreMenu);

	return (
		<MenuList>
			<GlobalMoreMenu
				productItemsData={productItemsData}
				appItemsData={appItemsData}
				menuItemComponent={menuItemComponent}
				customizeSidebarMenuItem={
					<CustomizeSidebarMenuItem
						items={items}
						shortcuts={shortcutItems}
						testId={CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID}
						appLabel={appLabel}
						onClick={onClickCustomizeSidebarMenuItem}
					/>
				}
				moreMenuItemLabel={intl.formatMessage(messages.moreMenuLabel)}
				peekingMenuIds={peekedItemId ? [peekedItemId] : undefined}
				testId={MORE_MENU_TEST_ID}
				onClickMoreMenuTrigger={onClickMoreMenuTrigger}
				triggerRef={l1DragAndDropEnabled ? moreButtonRef : undefined}
				triggerVisualContentRef={l1DragAndDropEnabled ? dropTargetRef : undefined}
				dropIndicator={l1DragAndDropEnabled ? dropIndicator : undefined}
			/>
		</MenuList>
	);
}
